import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { FaPencilAlt, FaCaretDown } from 'react-icons/fa';
import Swal from 'sweetalert2'; // For beautiful success alert

const ShippingAdd = () => {
  const navigate = useNavigate();

  // State to store the user ID (fetched from localStorage, used only for backend)
  const [addedBy, setAddedBy] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    company: 'Royal mail',
    service: '',
    usuallyDeparts: '',
    outboundDays: 0,
    inboundDays: 0,
    price: null
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch userLabId from localStorage when component mounts (for addedBy field in backend)
  useEffect(() => {
    const storedUserId = localStorage.getItem('userLabId');
    
    if (storedUserId) {
      setAddedBy(storedUserId);
    } else {
      setError('User information not found. Please log in again.');
    }
  }, []);

  // Handle input changes and convert numeric fields properly
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    // Convert to number or null for numeric fields
    if (name === 'outboundDays' || name === 'inboundDays' || name === 'price') {
      updatedValue = value === '' ? null : Number(value);
    }

    setFormData(prev => ({ ...prev, [name]: updatedValue }));
    setError(''); // Clear error when user starts typing
  };

  // Handle form submission and API call
  const handleSave = async () => {
    // Prevent submission if user ID is missing
    if (!addedBy) {
      setError('User information not found. Please log in again.');
      return;
    }

    // Validate required fields
    if (
      !formData.service ||
      formData.outboundDays == null ||
      formData.inboundDays == null
    ) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare request body including addedBy for backend
      const body = {
        ...formData,
        usuallyDeparts: formData.usuallyDeparts || '',
        addedBy: addedBy,
      };

      const res = await fetch('https://hms.backend.hiragroupcloud.com/api/shipping/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.success) {
        // Show success message using SweetAlert2 instead of plain alert
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Shipping Type Added Successfully!',
          confirmButtonColor: '#004aad',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate('/case-setting'); // Navigate after user clicks OK
        });
      } else {
        setError(data.message || 'Error saving shipping type');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ width: "1600px", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          {/* Main Content */}
          <div className="col-md-10">
            <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                Add Shipping Type
              </h1>
              <h2 style={{ fontSize: '18px', color: '#004aad', margin: '0 0 10px 0', fontWeight: 'normal' }}>
                Shipping Type Details
              </h2>

              {/* Error Message Display */}
              {error && (
                <div style={{ color: 'red', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '4px', marginBottom: '15px' }}>
                  {error}
                </div>
              )}

              {/* Form Container */}
              <div style={{ backgroundColor: '#f4f4f4', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '20px', borderTop: '2px solid #004aad' }}>
                
                {/* Company Selection */}
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Company <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <select 
                      name="company" 
                      value={formData.company} 
                      onChange={handleChange} 
                      style={{ width: '20%', padding: '8px 30px 8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white', appearance: 'none' }}
                    >
                      <option value="Royal mail">Royal mail</option>
                      <option value="Uber">Uber</option>
                      <option value="Local Driver">Local Driver</option>
                    </select>
                    <FaCaretDown style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#666', fontSize: '12px' }} />
                  </div>
                </div>

                {/* Service Name */}
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Service <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input 
                    type="text" 
                    name="service" 
                    value={formData.service} 
                    onChange={handleChange} 
                    placeholder="Enter service name"
                    style={{width: '15%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} 
                  />
                </div>

                {/* Usually Departs Time */}
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Usually Departs
                  </label>
                  <input 
                    type="text" 
                    name="usuallyDeparts" 
                    value={formData.usuallyDeparts} 
                    onChange={handleChange} 
                    placeholder="e.g., 10:00 AM"
                    style={{ width: '10%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} 
                  />
                </div>

                {/* Outbound Transit Days */}
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Outbound Transit <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="outboundDays"
                    value={formData.outboundDays ?? ''}
                    onChange={handleChange}
                    style={{ width: '60px', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', textAlign: 'center' }}
                    min="0"
                    step="1"
                  />
                  <span style={{ fontSize: '13px', color: '#666' }}>Days</span>
                </div>

                {/* Inbound Transit Days */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Inbound Transit Days <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="inboundDays"
                    value={formData.inboundDays ?? ''}
                    onChange={handleChange}
                    style={{ width: '60px', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', textAlign: 'center' }}
                    min="0"
                    step="1"
                  />
                </div>

                {/* Price (Optional) */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price ?? ''}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0.00"
                    style={{ width: '100px', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', textAlign: 'center' }}
                    min="0"
                  />
                  <span style={{ fontSize: '13px', color: '#666' }}>(Optional)</span>
                </div>

                {/* Save Button */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button 
                    onClick={handleSave} 
                    disabled={loading || !addedBy}
                    style={{ 
                      backgroundColor: addedBy ? '#004aad' : '#ccc', 
                      color: 'white', 
                      padding: '10px 20px', 
                      border: 'none', 
                      borderRadius: '4px', 
                      fontSize: '14px', 
                      cursor: (loading || !addedBy) ? 'not-allowed' : 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      fontWeight: 'bold',
                      opacity: loading ? 0.7 : 1 
                    }}
                  >
                    <FaPencilAlt /> {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingAdd;